/**
 * Application Policies - Single Source of Truth
 * 
 * This file defines all runtime policies for data access, caching, analytics,
 * networking, and error handling. All feature code should reference these
 * policies instead of hardcoding values.
 */

// =============================================================================
// DATA ACCESS POLICIES
// =============================================================================

export const DataAccessPolicy = {
  /**
   * Tables that can be queried from client (with RLS)
   * All other tables must go through edge functions
   */
  CLIENT_ACCESSIBLE_TABLES: [
    'articles',
    'events',
    'vehicle_reviews',
    'categories',
    'tags',
    'oems',
    'vehicles',
    'subscribers',
    'deal_profiles',
    'credit_tier_rates',
    'market_offers',
    'default_market_rates',
    'vehicle_trims',
  ] as const,

  /**
   * Tables requiring authenticated access
   */
  AUTH_REQUIRED_TABLES: [
    'deals',
    'dealer_leads',
    'dealer_accounts',
    'dealer_sales_team',
    'buyer_profiles',
    'buyer_notifications',
    'user_roles',
    'profiles',
    'audit_logs',
  ] as const,

  /**
   * Tables that are admin-only (require admin role)
   */
  ADMIN_ONLY_TABLES: [
    'user_roles',
    'audit_logs',
    'news_sources',
    'site_settings',
    'analytics_events',
    'api_metrics',
    'rate_limits',
  ] as const,

  /**
   * Maximum rows per query (Supabase default limit)
   */
  MAX_ROWS_PER_QUERY: 1000,

  /**
   * Default pagination size
   */
  DEFAULT_PAGE_SIZE: 20,
} as const;

// =============================================================================
// CACHING POLICIES
// =============================================================================

export const CachingPolicy = {
  /**
   * Stale times for React Query (milliseconds)
   */
  STALE_TIME: {
    /** Static content rarely changes */
    STATIC: 1000 * 60 * 60, // 1 hour
    /** Reference data (makes, models, trims) */
    REFERENCE_DATA: 1000 * 60 * 30, // 30 minutes
    /** Published content (articles, reviews) */
    CONTENT: 1000 * 60 * 5, // 5 minutes
    /** Hero/featured content - needs fresher data */
    HERO: 1000 * 30, // 30 seconds
    /** User-specific data (deals, notifications) */
    USER_DATA: 1000 * 60 * 2, // 2 minutes
    /** Real-time sensitive data (leads, dashboard) */
    REAL_TIME: 1000 * 30, // 30 seconds
  },

  /**
   * CDN cache TTLs (seconds) for edge functions
   */
  CDN_TTL: {
    STATIC_ASSETS: 86400, // 24 hours
    CONTENT_API: 300, // 5 minutes
    REFERENCE_DATA: 3600, // 1 hour
  },

  /**
   * Cache keys for content types
   */
  CACHE_KEYS: {
    ARTICLES: 'cached-articles',
    ARTICLE: 'cached-article',
    REVIEWS: 'cached-vehicle-reviews',
    REVIEW: 'cached-vehicle-review',
    EVENTS: 'cached-events',
    EVENT: 'cached-event',
    HERO: 'cached-hero-article',
    REFERENCE_DATA: 'reference-data',
    VEHICLE_MAKES: 'vehicle-makes',
    VEHICLE_MODELS: 'vehicle-models',
    VEHICLE_TRIMS: 'vehicle-trims',
  },
} as const;

// =============================================================================
// ANALYTICS POLICIES
// =============================================================================

export const AnalyticsPolicy = {
  /**
   * Event batching configuration
   */
  BATCH: {
    /** Flush interval in milliseconds */
    FLUSH_INTERVAL_MS: 2000,
    /** Maximum queue size before forced flush */
    MAX_QUEUE_SIZE: 10,
  },

  /**
   * Deduplication settings
   */
  DEDUPE: {
    /** Window in ms to consider events as duplicates */
    WINDOW_MS: 1000,
    /** Maximum entries in dedupe cache */
    MAX_CACHE_SIZE: 100,
  },

  /**
   * Allowed event types (whitelist)
   */
  ALLOWED_EVENTS: [
    'page_view',
    'deal_builder_start',
    'deal_builder_vehicle_selected',
    'deal_builder_payment_type_selected',
    'deal_builder_terms_changed',
    'deal_builder_saved',
    'deal_builder_submitted',
    'offer_viewed',
    'offer_accepted',
    'offer_rejected',
    'counter_offer_sent',
    'outbound_click_affiliate',
    'outbound_click_dealer_site',
    'outbound_click_call',
    'outbound_click_email',
    'article_view',
    'article_scroll_depth',
    'video_play',
    'video_complete',
    'review_view',
    'event_view',
    'search_performed',
    'filter_applied',
    'newsletter_signup',
    'dealer_contact_initiated',
    'deal_confirmed',
  ] as const,

  /**
   * Scroll depth milestones to track
   */
  SCROLL_MILESTONES: [25, 50, 75, 100],

  /**
   * Never block navigation for analytics
   */
  FIRE_AND_FORGET: true,
} as const;

// =============================================================================
// NETWORKING POLICIES
// =============================================================================

export const NetworkingPolicy = {
  /**
   * Default request timeout in milliseconds
   */
  DEFAULT_TIMEOUT_MS: 10000,

  /**
   * Timeout for content API calls
   */
  CONTENT_API_TIMEOUT_MS: 5000,

  /**
   * Maximum retries for failed requests
   */
  MAX_RETRIES: 3,

  /**
   * Retry delay base (exponential backoff)
   */
  RETRY_DELAY_BASE_MS: 1000,

  /**
   * Endpoints that should NOT be retried
   */
  NO_RETRY_ENDPOINTS: [
    '/api/functions/analytics-ingest',
    '/api/functions/send-notification',
  ],

  /**
   * Same-origin proxy configuration
   * Only used in production (not in Lovable preview)
   */
  SAME_ORIGIN_PROXY: {
    /** Hostnames where same-origin proxy is disabled */
    DISABLED_HOSTS: ['lovable.app', 'lovableproject.com', 'localhost'],
    /** REST API path prefix */
    REST_PATH: '/api/rest',
    /** Functions path prefix */
    FUNCTIONS_PATH: '/api/functions',
  },

  /**
   * Standard headers for API requests
   */
  STANDARD_HEADERS: {
    'Content-Type': 'application/json',
  },
} as const;

// =============================================================================
// ERROR HANDLING POLICIES
// =============================================================================

export const ErrorHandlingPolicy = {
  /**
   * Standard error format
   */
  ERROR_FORMAT: {
    code: 'string',
    message: 'string',
    details: 'object | null',
    timestamp: 'string',
  },

  /**
   * Error codes
   */
  ERROR_CODES: {
    // Auth errors
    AUTH_EXPIRED: 'AUTH_EXPIRED',
    AUTH_REQUIRED: 'AUTH_REQUIRED',
    AUTH_FORBIDDEN: 'AUTH_FORBIDDEN',
    
    // Validation errors
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
    
    // Database errors
    RLS_VIOLATION: 'RLS_VIOLATION',
    NOT_FOUND: 'NOT_FOUND',
    DUPLICATE_KEY: 'DUPLICATE_KEY',
    
    // Network errors
    NETWORK_ERROR: 'NETWORK_ERROR',
    TIMEOUT: 'TIMEOUT',
    
    // Server errors
    INTERNAL_ERROR: 'INTERNAL_ERROR',
  },

  /**
   * User-friendly error messages
   */
  USER_MESSAGES: {
    AUTH_EXPIRED: 'Your session has expired. Please log in again.',
    AUTH_REQUIRED: 'Please log in to continue.',
    AUTH_FORBIDDEN: 'You do not have permission to perform this action.',
    VALIDATION_FAILED: 'Please check your input and try again.',
    RLS_VIOLATION: 'Permission denied. Please try logging out and back in.',
    NOT_FOUND: 'The requested item was not found.',
    NETWORK_ERROR: 'Network error. Please check your connection.',
    TIMEOUT: 'Request timed out. Please try again.',
    INTERNAL_ERROR: 'Something went wrong. Please try again later.',
  },

  /**
   * Logging levels
   */
  LOG_LEVELS: {
    ERROR: 'error',
    WARN: 'warn',
    INFO: 'info',
    DEBUG: 'debug',
  },

  /**
   * Errors that should be logged to console in production
   */
  ALWAYS_LOG: ['AUTH_EXPIRED', 'RLS_VIOLATION', 'INTERNAL_ERROR'],
} as const;

// =============================================================================
// RATE LIMITING POLICIES
// =============================================================================

export const RateLimitPolicy = {
  /**
   * Rate limits by endpoint (requests per window)
   */
  LIMITS: {
    /** Analytics ingestion */
    ANALYTICS_INGEST: { requests: 100, windowMs: 60000 },
    /** Newsletter signup */
    NEWSLETTER_SIGNUP: { requests: 5, windowMs: 60000 },
    /** Contact form */
    CONTACT_FORM: { requests: 3, windowMs: 60000 },
    /** Deal submission */
    DEAL_SUBMIT: { requests: 10, windowMs: 60000 },
    /** Admin purge operations */
    ADMIN_PURGE: { requests: 5, windowMs: 3600000 }, // 5 per hour
  },

  /**
   * Default rate limit for unspecified endpoints
   */
  DEFAULT: { requests: 60, windowMs: 60000 },
} as const;

// =============================================================================
// SECURITY POLICIES
// =============================================================================

export const SecurityPolicy = {
  /**
   * Allowed admin emails (single source of truth)
   * Note: Also defined in src/lib/adminConfig.ts for frontend
   * and supabase/functions/_shared/admin-config.ts for edge functions
   */
  ALLOWED_ADMIN_EMAILS: ['akhuda@gmail.com'] as const,

  /**
   * Token expiration times
   */
  TOKEN_EXPIRY: {
    /** Invite token validity */
    INVITE_TOKEN_HOURS: 72,
    /** Setup token validity */
    SETUP_TOKEN_HOURS: 24,
    /** Deal confirmation window */
    DEAL_CONFIRMATION_HOURS: 24,
  },

  /**
   * Password requirements
   */
  PASSWORD_REQUIREMENTS: {
    MIN_LENGTH: 8,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBER: true,
    REQUIRE_SPECIAL: false,
  },

  /**
   * PII fields that should be masked in logs
   */
  PII_FIELDS: [
    'email',
    'phone',
    'mobile_phone',
    'full_name',
    'address',
    'zip_code',
  ],
} as const;

// =============================================================================
// FEATURE FLAGS
// =============================================================================

export const FeatureFlags = {
  /**
   * Enable same-origin proxy (disable in Lovable preview)
   */
  SAME_ORIGIN_PROXY: true,

  /**
   * Enable analytics batching
   */
  ANALYTICS_BATCHING: true,

  /**
   * Enable content caching via edge function
   */
  CONTENT_CACHING: true,

  /**
   * Enable real-time subscriptions
   */
  REALTIME_SUBSCRIPTIONS: true,

  /**
   * Enable pull-to-refresh on mobile
   */
  PULL_TO_REFRESH: true,
} as const;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type DataAccessTable = typeof DataAccessPolicy.CLIENT_ACCESSIBLE_TABLES[number];
export type AuthRequiredTable = typeof DataAccessPolicy.AUTH_REQUIRED_TABLES[number];
export type AnalyticsEventType = typeof AnalyticsPolicy.ALLOWED_EVENTS[number];
export type ErrorCode = keyof typeof ErrorHandlingPolicy.ERROR_CODES;
